import React, { useState } from 'react'
import { connect } from 'react-redux'
import AdminHeader from '../AdminHeader'
import { AdminUI } from '../../../constants/admin/adminUi'
import { AppState } from '../../../reducers'
import './terminal.less'
import { UNEDITABLE_FIELDS_OF_TERMINAL as unEditableFieldsOrigin } from '../../../constants/admin/adminListOfTerminals'
import { saveTerminalAction } from '../../../actions/admin/adminTerminals'
import { setAdminUIAction } from '../../../actions/admin/adminUi'
import _ from 'lodash'
import { Terminal } from '../../../types/terminal'

const langs = ['en', 'ru', 'et', 'fi', 'sv', 'lv', 'pl']
const fieldName: { [key: string]: string } = {
  info: 'Info',
  location: 'Location',
  checkInClosingOffset: 'Checkin closed before departure (min)',
  allowReprint: 'Allow to re-print SWB',
  allowVehicleCardPrint: 'Allow to print vehicle card',
  firstInstruction: 'First SWB instruction',
  secondInstruction: 'Second SWB instruction',
}

const mapStateToProps = (state: AppState) => ({
  terminal: state.adminTerminals.terminalEdit,
})

const mapDispatchToProps = {
  setAdminUI: setAdminUIAction,
  saveChanges: saveTerminalAction,
}

type DispatchProps = typeof mapDispatchToProps

type StateProps = ReturnType<typeof mapStateToProps>
type Props = StateProps & DispatchProps

const unEditableFields =
  (unEditableFieldsOrigin && typeof unEditableFieldsOrigin === 'object' && unEditableFieldsOrigin) || []

const UnEditableFields = ({ terminalItem }: { terminalItem: Terminal }) => {
  const filtered = _.pick(terminalItem, unEditableFields)

  return (
    <React.Fragment>
      <div className="unEditable-terminal-row">
        {Object.entries(filtered).map(([key, value]) => (
          <div key={'row' + key} className="unEditable-terminal-item">
            <div key={'name' + key} className="unEditable-terminal-field-name">
              {key}:{' '}
            </div>
            <div key={'field' + key} className="unEditable-terminal-field-value">
              {value}
            </div>
          </div>
        ))}
        <div className="unEditable-terminal-row-space"></div>
      </div>
    </React.Fragment>
  )
}

const InputFields = ({
  terminalItem,
  setTerminalsItem,
}: {
  terminalItem: Terminal
  setTerminalsItem: (terminal: Terminal) => void
}) => {
  const filtered = _.omit(terminalItem, unEditableFields)
  const filteredWithoutLangs = _.pickBy(filtered, _.isString)
  const filteredCheckboxes = _.omit(_.pickBy(filtered, _.isBoolean), 'checkInClosingOffset')
  const filteredWithLangs = _.pickBy(filtered, _.isObject)

  const onChangeField = (e: string | boolean | number, key: string) => {
    setTerminalsItem({ ...terminalItem, [key]: e })
  }

  const onChangeLangField = (e: string, value: any, key: string, currentLang: string) => {
    const changed = { ...terminalItem, [key]: { ...value, [currentLang]: e } }
    setTerminalsItem(changed)
  }

  return (
    <React.Fragment>
      {Object.entries(filteredWithoutLangs).map(([key, value], index) => (
        <div key={'row' + key} className="terminal-inputs-row">
          <div className="first-column terminal-key-of-input">
            <div className="terminal-key-of-input-inner">{fieldName[key] || key}</div>
          </div>
          <div
            className={
              index === 0 ? 'border-top cell-without-langs terminal-input' : 'cell-without-langs terminal-input'
            }
          >
            <textarea
              className="terminal-field-input"
              value={value}
              onChange={(e) => onChangeField(e.target.value, key)}
            />
          </div>
        </div>
      ))}
      <div className="terminal-inputs-row one-line-row">
        <div className="first-column terminal-key-of-input">
          <div className="terminal-key-of-input-inner">{fieldName['checkInClosingOffset']}</div>
        </div>
        <div className={'cell-without-langs terminal-input'}>
          <input
            className="terminal-field-input"
            onChange={(e) => {
              if (!isNaN(+e.target.value)) onChangeField(+e.target.value, 'checkInClosingOffset')
            }}
            value={terminalItem.checkInClosingOffset}
          />
        </div>
      </div>
      {Object.entries(filteredCheckboxes).map(([key, value]) => (
        <div key={'row' + key} className="terminal-inputs-row for-checkbox-input">
          <div key={'name' + key} className="first-column terminal-key-of-input">
            <div key={'name-inner' + key} className="terminal-key-of-input-inner">
              {fieldName[key] || key}
            </div>
          </div>
          <div key={'input' + key} className="cell-without-langs terminal-checkbox-cell">
            <input
              type="checkbox"
              key={'input-inner' + key}
              className="terminal-checkbox-input"
              checked={value}
              onChange={(e) => onChangeField(e.target.checked, key)}
            />
          </div>
        </div>
      ))}
      <div className="terminal-header-row">
        <div className="first-column"></div>
        {langs.map((currentLang) => (
          <div key={'header' + currentLang} className="cell">
            {currentLang}
          </div>
        ))}
      </div>
      {Object.entries(filteredWithLangs).map(([key, value]) => (
        <div key={'row' + key} className="terminal-inputs-row langs-row">
          <div key={'name' + key} className="first-column terminal-key-of-input">
            <div key={'name-inner' + key} className="terminal-key-of-input-inner">
              {fieldName[key] || key}
            </div>
          </div>
          {langs.map((currentLang: any) => (
            <div key={'lang' + key + currentLang} className="cell terminal-input">
              <textarea
                key={'input' + key}
                className="terminal-field-input"
                value={value[currentLang]}
                onChange={(e) => onChangeLangField(e.target.value, value, key, currentLang)}
              />
            </div>
          ))}
        </div>
      ))}
    </React.Fragment>
  )
}

const Component: React.FC<Props> = ({ terminal, saveChanges, setAdminUI }: Props) => {
  const [terminalItem, setTerminalsItem] = useState(terminal)
  const save = () => {
    if (!terminal) {
      return
    }
    const saveData = _.omit(terminalItem, unEditableFields)
    saveChanges({ id: terminal.id, ...saveData })
  }

  return (
    <React.Fragment>
      <AdminHeader />
      {terminalItem && (
        <div className="admin-terminal-main">
          <div className="admin-terminal-title">Terminal info</div>
          <div className="admin-terminal-info">
            <UnEditableFields terminalItem={terminalItem} />
            <InputFields terminalItem={terminalItem} setTerminalsItem={setTerminalsItem} />
          </div>
          <div className="admin-terminal-buttons">
            <button className="admin-terminal-button-cancel" onClick={() => setAdminUI(AdminUI.ALL_TERMINALS)}>
              Cancel
            </button>
            <button className="admin-terminal-button-save" onClick={save}>
              Save
            </button>
          </div>
        </div>
      )}
    </React.Fragment>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(Component)
